import { Injectable } from "@angular/core";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import { AppService } from "./app.service";
import { ConfigService } from "./app-config.service";
import { SystemService } from "./system.service";

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number; // Mapeado desde response.count o response.totalCount
  summary?: any[];
  groupCount?: number;
}

export interface RemoteDataSourceOptions {
  apiUrl: string;
  additionalFilters?: { [key: string]: { value: any; operator?: string } };
  useQueryKit?: boolean;
  defaultSortField?: string; // Campo por defecto para ordenamiento
  searchableFields?: string[]; // Campos que se pueden buscar en búsqueda global
  columns?: any[]; // Columnas de la tabla para extraer campos searchables automáticamente
}

@Injectable()
export class PaginatedService<T> {

  constructor(
    private appService: AppService,
    private configService: ConfigService,
    private systemService: SystemService
  ) {}

  /**
   * Crea un DataSource remoto para DevExtreme con paginación, filtrado y ordenamiento
   * @param options Configuración del DataSource
   * @returns DataSource configurado para operaciones remotas
   */
  createRemoteDataSource(options: RemoteDataSourceOptions): DataSource {
    const store = new CustomStore({
      key: 'id',
      load: (loadOptions: any) => {
        return this.loadData(loadOptions, options);
      }
    });

    return new DataSource({
      store: store,
      paginate: true,
      pageSize: this.systemService.tablePagination
    });
  }

  /**
   * Carga datos desde el backend con paginación, filtrado y ordenamiento
   *
   * Parámetros enviados al backend:
   * - pageNumber: Número de página (requerido para paginación)
   * - pageSize: Tamaño de página (requerido para paginación)
   * - sortOrder: Campos con direcciones de ordenamiento (ej: "name asc, Age desc") - REQUERIDO
   * - group: Campos de agrupamiento (ej: "category,status")
   * - groupOrder: Direcciones de agrupamiento (ej: "asc,desc")
   * - Filters: Sintaxis QueryKit para filtros
   *
   * Formato de respuesta esperado del backend:
   *
   * Headers HTTP:
   * X-Pagination: {
   *   totalCount: number,      // Total de registros (para paginación)
   *   pageSize: number,        // Tamaño de página
   *   currentPageSize: number, // Tamaño actual de página
   *   currentStartIndex: number, // Índice de inicio actual
   *   currentEndIndex: number,   // Índice de fin actual
   *   pageNumber: number,      // Número de página actual
   *   totalPages: number,      // Total de páginas
   *   hasPrevious: boolean,    // Tiene página anterior
   *   hasNext: boolean         // Tiene página siguiente
   * }
   *
   * Cuerpo de respuesta:
   * {
   *   data: T[],           // Array de registros de la página actual
   *   summary?: any[],     // Resúmenes (opcional)
   *   groupCount?: number  // Conteo de grupos (opcional)
   * }
   *
   * @param loadOptions Opciones de carga de DevExtreme
   * @param options Configuración del DataSource
   * @returns Promise con los datos paginados
   */
  private async loadData(
    loadOptions: any,
    options: RemoteDataSourceOptions
  ): Promise<PaginatedResponse<T>> {
    try {
      // Construir URL base
      let url = options.apiUrl;
      const queryParams: string[] = [];

      // Agregar paginación
      if (loadOptions.skip !== undefined && loadOptions.take !== undefined) {
        const pageNumber = Math.floor(loadOptions.skip / loadOptions.take) + 1;
        queryParams.push(`pageNumber=${pageNumber}`);
        queryParams.push(`pageSize=${loadOptions.take}`);
      }

      // Agregar ordenamiento - Solo SortOrder es requerido por el backend
      if (loadOptions.sort && loadOptions.sort.length > 0) {
        // Agregar SortOrder (requerido por el backend) - formato: "field direction"
        const sortOrder = loadOptions.sort.map((sort: any) =>
          `${sort.selector} ${sort.desc ? 'desc' : 'asc'}`
        ).join(', ');
        queryParams.push(`sortOrder=${encodeURIComponent(sortOrder)}`);
      } else {
        // Si no hay ordenamiento específico, usar ordenamiento por defecto con el primer campo
        const defaultSortField = options.defaultSortField || 'id';
        queryParams.push(`sortOrder=${encodeURIComponent(defaultSortField + ' asc')}`);
      }

      // Agregar agrupamiento
      if (loadOptions.group && loadOptions.group.length > 0) {
        const groupParams = loadOptions.group.map((group: any) => group.selector).join(',');
        queryParams.push(`group=${encodeURIComponent(groupParams)}`);

        // Agregar GroupOrder si es necesario
        const groupOrder = loadOptions.group.map((group: any) =>
          group.desc ? 'desc' : 'asc'
        ).join(',');
        queryParams.push(`groupOrder=${encodeURIComponent(groupOrder)}`);
      }

      // Agregar filtros
      if (loadOptions.filter && loadOptions.filter.length > 0) {
        const filterSyntax = this.buildFilterSyntax(loadOptions.filter, options);
        if (filterSyntax) {
          queryParams.push(`Filters=${encodeURIComponent(filterSyntax)}`);
        }
      }

      // Agregar filtros adicionales si existen
      if (options.additionalFilters && Object.keys(options.additionalFilters).length > 0) {
        const additionalFilterSyntax = this.buildAdditionalFiltersSyntax(options.additionalFilters);
        if (additionalFilterSyntax) {
          // Si ya hay filtros, combinar con AND
          const existingFilters = queryParams.find(param => param.startsWith('Filters='));
          if (existingFilters) {
            const existingFilterValue = existingFilters.split('=')[1];
            const combinedFilters = `${decodeURIComponent(existingFilterValue)} && ${additionalFilterSyntax}`;
            queryParams[queryParams.indexOf(existingFilters)] = `Filters=${encodeURIComponent(combinedFilters)}`;
          } else {
            queryParams.push(`Filters=${encodeURIComponent(additionalFilterSyntax)}`);
          }
        }
      }

      // Validar parámetros requeridos para operaciones remotas
      this.validateRequiredParams(queryParams, loadOptions);

      // Construir URL final
      if (queryParams.length > 0) {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}${queryParams.join('&')}`;
      }

      // Realizar petición con headers
      const response = await this.appService.getWithHeaders(url);

      // Extraer información de paginación de los headers
      let totalCount = 0;
      const paginationMetadata = this.extractPaginationFromHeaders(response);

      if (paginationMetadata) {
        totalCount = paginationMetadata.totalCount || 0;
      }

      // Fallback: buscar en el cuerpo de la respuesta
      if (totalCount === 0) {
        totalCount = (response as any).count || (response as any).totalCount || 0;
      }

      // Formatear respuesta según el formato esperado por DevExtreme
      return {
        data: response.data || [],
        totalCount: totalCount,
        summary: (response as any).summary,
        groupCount: (response as any).groupCount
      };

    } catch (error) {
      console.error('Error loading remote data:', error);
      throw error;
    }
  }

  /**
   * Construye la sintaxis de filtros para QueryKit desde los filtros de DevExtreme
   * @param filters Filtros de DevExtreme
   * @param options Opciones del DataSource
   * @returns Sintaxis de QueryKit
   */
  private buildFilterSyntax(filters: any[], options?: RemoteDataSourceOptions): string {
    if (!filters || filters.length === 0) {
      return '';
    }

    // Detectar si es un filtro de búsqueda global
    const isGlobalSearch = this.isGlobalSearchFilter(filters);

    if (isGlobalSearch) {
      return this.buildGlobalSearchFilter(filters, options);
    }

    const conditions: string[] = [];

    filters.forEach(filter => {
      if (Array.isArray(filter)) {
        // Filtros anidados (OR)
        const nestedConditions = filter.map(f => this.buildSingleFilter(f));
        conditions.push(`(${nestedConditions.join(' || ')})`);
      } else {
        // Filtro simple
        conditions.push(this.buildSingleFilter(filter));
      }
    });

    return conditions.join(' && ');
  }

  /**
   * Detecta si los filtros representan una búsqueda global
   * @param filters Filtros de DevExtreme
   * @returns true si es una búsqueda global
   */
  private isGlobalSearchFilter(filters: any[]): boolean {
    // Una búsqueda global típicamente tiene múltiples filtros con el mismo valor
    // y operadores 'contains' en diferentes campos
    if (filters.length < 3) {
      return false;
    }

    // Extraer solo los filtros (arrays), ignorando los operadores 'or'
    const filterArrays = filters.filter(item => Array.isArray(item));

    if (filterArrays.length < 2) {
      return false;
    }

    const firstValue = filterArrays[0]?.[2];

    if (!firstValue || typeof firstValue !== 'string') {
      return false;
    }

    // Verificar si todos los filtros tienen el mismo valor y operador 'contains'
    return filterArrays.every(filter => {
      const hasLength = filter.length >= 3;
      const hasContains = filter[1] === 'contains';
      const hasSameValue = filter[2] === firstValue;

      return hasLength && hasContains && hasSameValue;
    });
  }

  /**
   * Construye un filtro de búsqueda global usando OR entre campos
   * @param filters Filtros de búsqueda global
   * @param options Opciones del DataSource
   * @returns Sintaxis de QueryKit para búsqueda global
   */
  private buildGlobalSearchFilter(filters: any[], options?: RemoteDataSourceOptions): string {
    if (!filters || filters.length === 0) {
      return '';
    }

    // Extraer solo los filtros (arrays), ignorando los operadores 'or'
    const filterArrays = filters.filter(item => Array.isArray(item));

    if (filterArrays.length === 0) {
      return '';
    }

    const searchValue = filterArrays[0][2];
    let searchFields: string[] = [];

    // Si hay campos configurables, usarlos; si no, extraer automáticamente
    if (options?.searchableFields && options.searchableFields.length > 0) {
      searchFields = options.searchableFields;
    } else {
      // Intentar extraer de las columnas primero, luego de los filtros
      searchFields = this.extractSearchableFields(options);

      // Si no se encontraron campos en las columnas, extraer de los filtros
      if (searchFields.length === 0) {
        filterArrays.forEach(filter => {
          if (filter[0]) {
            const field = filter[0];
            if (!searchFields.includes(field)) {
              searchFields.push(field);
            }
          }
        });
      }
    }

    // Crear condiciones OR para cada campo
    const orConditions = searchFields.map(field =>
      `${field} @= "${this.escapeValue(searchValue)}"`
    );

    return `(${orConditions.join(' || ')})`;
  }

  /**
   * Extrae los campos searchables de las columnas de la tabla
   * @param options Opciones del DataSource
   * @returns Array de campos que permiten búsqueda
   */
  private extractSearchableFields(options?: RemoteDataSourceOptions): string[] {
    const searchableFields: string[] = [];

    if (options?.columns && options.columns.length > 0) {
      // Buscar en las columnas de la tabla
      options.columns.forEach(column => {
        if (column.allowSearch && column.dataField) {
          searchableFields.push(column.dataField);
        }
      });
    }

    return searchableFields;
  }

  /**
   * Construye la sintaxis para un filtro individual
   * @param filter Filtro individual
   * @returns Sintaxis de QueryKit para el filtro
   */
  private buildSingleFilter(filter: any): string {
    if (!filter || !filter[0]) {
      return '';
    }

    const field = filter[0];
    const operator = filter[1];
    const value = filter[2];

    // Mapear operadores de DevExtreme a QueryKit
    const operatorMap: { [key: string]: string } = {
      '=': '==',
      '<>': '!=',
      '>': '>',
      '<': '<',
      '>=': '>=',
      '<=': '<=',
      'contains': '@=',
      'notcontains': '!@=',
      'startswith': '@=',
      'endswith': '@=',
      'in': '^^',
      'notin': '!^^'
    };

    const queryKitOperator = operatorMap[operator] || '==';

    // Formatear valor según el tipo
    let formattedValue: string;
    if (Array.isArray(value)) {
      // Para operadores IN/NOT IN
      const values = value.map(v => `"${this.escapeValue(String(v))}"`).join(', ');
      formattedValue = `[${values}]`;
    } else if (typeof value === 'string') {
      formattedValue = `"${this.escapeValue(value)}"`;
    } else if (typeof value === 'boolean') {
      formattedValue = value ? 'true' : 'false';
    } else {
      formattedValue = String(value);
    }

    return `${field} ${queryKitOperator} ${formattedValue}`;
  }

  /**
   * Construye la sintaxis para filtros adicionales
   * @param additionalFilters Filtros adicionales
   * @returns Sintaxis de QueryKit
   */
  private buildAdditionalFiltersSyntax(additionalFilters: { [key: string]: { value: any; operator?: string } }): string {
    const conditions: string[] = [];

    Object.keys(additionalFilters).forEach(key => {
      const filter = additionalFilters[key];
      const operator = filter.operator || '==';
      let value = filter.value;

      if (typeof value === 'string') {
        value = `"${value}"`;
      } else if (typeof value === 'boolean') {
        value = value ? 'true' : 'false';
      }

      conditions.push(`${key} ${operator} ${value}`);
    });

    return conditions.join(' && ');
  }

  /**
   * Escapa caracteres especiales en valores de string
   * @param value Valor a escapar
   * @returns Valor escapado
   */
  private escapeValue(value: string): string {
    return value.replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/\t/g, '\\t');
  }

  /**
   * Extrae la información de paginación de los headers HTTP
   * @param response Respuesta del backend
   * @returns Información de paginación o null si no se encuentra
   */
  private extractPaginationFromHeaders(response: any): any {
    try {
      if (response.headers) {
        // Usar el método get() de HttpHeaders para acceder al header x-pagination
        const paginationHeader = response.headers.get('x-pagination');

        if (paginationHeader) {
          return JSON.parse(paginationHeader);
        }
      }
    } catch (error) {
      console.error('Error parsing X-Pagination header:', error);
    }

    return null;
  }

  /**
   * Valida que los parámetros requeridos estén presentes para operaciones remotas
   * @param queryParams Parámetros de consulta construidos
   * @param loadOptions Opciones de carga de DevExtreme
   */
  private validateRequiredParams(queryParams: string[], loadOptions: any): void {
    // Validar que siempre se incluyan pageNumber y pageSize para operaciones remotas
    const hasPageNumber = queryParams.some(param => param.startsWith('pageNumber='));
    const hasPageSize = queryParams.some(param => param.startsWith('pageSize='));

    if (!hasPageNumber || !hasPageSize) {
      console.warn('PaginatedService: pageNumber y pageSize son requeridos para operaciones remotas');
    }

    // Validar que sortOrder siempre esté presente (es requerido por el backend)
    const hasSortOrder = queryParams.some(param => param.startsWith('sortOrder='));

    if (!hasSortOrder) {
      console.error('PaginatedService: sortOrder es SIEMPRE requerido por el backend');
    }

    // Validar que si hay agrupamiento, también se incluya groupOrder
    const hasGroup = queryParams.some(param => param.startsWith('group='));
    const hasGroupOrder = queryParams.some(param => param.startsWith('groupOrder='));

    if (hasGroup && !hasGroupOrder) {
      console.warn('PaginatedService: groupOrder es requerido cuando se especifica group');
    }
  }
}
